// Enhanced API client designed to work seamlessly with Hono framework
// Provides type-safe, modular API access matching the Hono route structure

// Clerk integration is handled via token parameter
import type {
	ApiResponse,
	CreateTaskRequest,
	Document,
	GetTasksRequest,
	PayinSlip,
	PayinSlipKey,
	PaymentUploadRequest,
	Task,
	UpdateDocumentContentRequest,
	UpdateDocumentStatusRequest,
	UploadResponse,
	WFMPatchRequest,
	WFMPatchResult,
	WFMSearchRequest,
	WFMSearchResult,
	WorkflowUpdateResponse,
} from '../../shared/types'
import type {
	LEDCase,
	LEDDocument,
	LEDDashboardData,
	LEDTasksResponse,
	LEDTaskDetailResponse,
	CreateLEDCaseRequest,
	UpdateLEDCaseRequest,
	LEDStatusUpdateRequest,
} from '../../shared/types/led'
import type {
	LitigationCase,
	SLANode,
	GanttTask,
	AssigneeWorkload,
	FlowTemplate,
	SLANotification,
	SLAMetrics,
} from '../../shared/types/sla'

// Case compliance type specific to the API
interface CaseComplianceStatus {
	case_id: string
	isCompliant: boolean
	complianceType: 'template_based' | 'legacy_hardcoded' | 'mixed' | 'unknown'
	issues: string[]
	recommendations: string[]
	templateCode?: string
	litigationCase: LitigationCase
}

// Statistics types
export interface DailyStats {
	date: string
	uploads_count: number
	approvals_count: number
	rejections_count: number
	total_activities: number
}

export interface StatisticsSummary {
	period: {
		days: number
		total_uploads: number
		total_approvals: number
		total_rejections: number
		total_activities: number
		active_days: number
	}
	today: {
		uploads_count: number
		approvals_count: number
		rejections_count: number
	}
}

export interface DocumentTypeStats {
	document_type: string
	total_uploads: number
	total_approvals: number
	total_rejections: number
	total_activities: number
}

export interface DocumentTypeStatistics {
	summary: DocumentTypeStats[]
	daily: Array<{
		date: string
		document_type: string
		uploads_count: number
		approvals_count: number
		rejections_count: number
	}>
}

export interface UserStats {
	user_id: string
	email?: string
	first_name?: string
	last_name?: string
	image_url?: string
	total_uploads: number
	total_approvals: number
	total_rejections: number
	total_activities: number
}

export interface UserDetailedStats extends UserStats {
	document_types: Array<{
		document_type: string
		uploads_count: number
		approvals_count: number
		rejections_count: number
		total_activities: number
	}>
}

/**
 * Base API client class with common request handling
 */
class BaseApiClient {
	protected readonly baseUrl: string
	protected getToken?: (retryAttempt?: number) => Promise<string | null>
	protected getCompany?: () => string | null

	constructor(
		baseUrl = '/api', 
		getToken?: (retryAttempt?: number) => Promise<string | null>,
		getCompany?: () => string | null
	) {
		this.baseUrl = baseUrl
		this.getToken = getToken
		this.getCompany = getCompany
	}

	/**
	 * Generic request handler with improved error handling, type safety, and automatic token refresh
	 */
	protected async request<T>(endpoint: string, options: RequestInit = {}, retryAttempt = 0): Promise<ApiResponse<T>> {
		const url = `${this.baseUrl}${endpoint}`

		const defaultHeaders: Record<string, string> = {
			'Content-Type': 'application/json',
		}

		// Add authentication token if available
		if (this.getToken) {
			try {
				const token = await this.getToken(retryAttempt)
				if (token) {
					defaultHeaders['Authorization'] = `Bearer ${token}`
				}
				console.log('[API] Request authentication:', {
					hasToken: !!token,
					tokenLength: token?.length || 0,
					endpoint,
					method: options.method || 'GET',
					retryAttempt
				})
			} catch (error) {
				console.warn('[API] Failed to get authentication token:', error)
				// Continue with request without token
			}
		}

		// Add company header if available
		if (this.getCompany) {
			const company = this.getCompany()
			if (company) {
				defaultHeaders['X-Company-ID'] = company
			}
		}

		const config: RequestInit = {
			headers: {
				...defaultHeaders,
				...options.headers,
			},
			...options,
		}

		console.log('[API] Making request:', {
			method: config.method || 'GET',
			url,
			hasBody: !!config.body,
			bodyLength: config.body ? String(config.body).length : 0,
			headers: Object.keys(defaultHeaders),
			retryAttempt,
			timestamp: new Date().toISOString()
		})

		try {
			const response = await fetch(url, config)
			
			console.log('[API] Response received:', {
				status: response.status,
				statusText: response.statusText,
				ok: response.ok,
				url,
				retryAttempt,
				timestamp: new Date().toISOString()
			})

			// Handle 401 responses with automatic token refresh
			if (response.status === 401 && retryAttempt === 0 && this.getToken) {
				console.log('[API] Got 401 response, attempting token refresh and retry')
				
				try {
					// Parse error response to check if it's a token expiration issue
					const errorText = await response.text()
					const isTokenExpired = errorText.includes('expired') || errorText.includes('Token expired')
					
					if (isTokenExpired) {
						console.log('[API] Token expired, retrying with force refresh')
						return this.request<T>(endpoint, options, 1) // Retry once with force refresh
					} else {
						console.log('[API] 401 error but not token expiration:', errorText)
						throw new Error(`HTTP ${response.status}: ${response.statusText}`)
					}
				} catch (parseError) {
					console.warn('[API] Failed to parse 401 error response, attempting retry anyway')
					return this.request<T>(endpoint, options, 1) // Retry once with force refresh
				}
			}

			if (!response.ok) {
				const errorText = await response.text()
				console.error('[API] HTTP error response body:', {
					status: response.status,
					statusText: response.statusText,
					body: errorText,
					retryAttempt
				})
				throw new Error(`HTTP ${response.status}: ${response.statusText}`)
			}

			const data = (await response.json()) as ApiResponse<T>
			console.log('[API] Parsed response data:', {
				success: data.success,
				hasData: !!data.data,
				error: data.error,
				url,
				retryAttempt
			})
			return data
		} catch (error) {
			console.error('[API] Request exception:', {
				method: config.method || 'GET',
				url,
				error,
				errorMessage: error instanceof Error ? error.message : 'Unknown error',
				retryAttempt,
				timestamp: new Date().toISOString()
			})
			throw error
		}
	}

	/**
	 * GET request helper
	 */
	protected get<T>(endpoint: string): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { method: 'GET' })
	}

	/**
	 * POST request helper
	 */
	protected post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			method: 'POST',
			body: data ? JSON.stringify(data) : undefined,
		})
	}

	/**
	 * PUT request helper
	 */
	protected put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			method: 'PUT',
			body: data ? JSON.stringify(data) : undefined,
		})
	}

	/**
	 * DELETE request helper
	 */
	protected delete<T>(endpoint: string): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { method: 'DELETE' })
	}

	/**
	 * Raw fetch method for direct access to fetch API
	 * This provides flexibility for components that need custom request handling
	 */
	public async rawFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
		const url = `${this.baseUrl}${endpoint}`

		const defaultHeaders: Record<string, string> = {
			'Content-Type': 'application/json',
		}

		// Add authentication token if available (no retry for rawFetch)
		if (this.getToken) {
			try {
				const token = await this.getToken(0) // Always use fresh token for rawFetch
				if (token) {
					defaultHeaders['Authorization'] = `Bearer ${token}`
				}
			} catch (error) {
				console.warn('Failed to get authentication token:', error)
				// Continue with request without token
			}
		}

		// Add company header if available
		if (this.getCompany) {
			const company = this.getCompany()
			if (company) {
				defaultHeaders['X-Company-ID'] = company
			}
		}

		const config: RequestInit = {
			...options,
			headers: {
				...defaultHeaders,
				...options.headers,
			},
		}

		return fetch(url, config)
	}
}

/**
 * Task-related API operations
 * Matches: src/workers/routes/tasks.ts
 */
class TasksApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Get tasks with optional pagination and filtering
	 */
	async getTasks(params?: GetTasksRequest): Promise<ApiResponse<Task[]>> {
		const searchParams = new URLSearchParams()

		if (params?.page) {
			searchParams.append('page', params.page.toString())
		}
		if (params?.limit) {
			searchParams.append('limit', params.limit.toString())
		}
		if (params?.document_type) {
			searchParams.append('document_type', params.document_type)
		}
		if (params?.status) {
			searchParams.append('status', params.status)
		}

		const queryString = searchParams.toString()
		const endpoint = queryString ? `/tasks?${queryString}` : '/tasks'

		return this.get<Task[]>(endpoint)
	}

	/**
	 * Get specific task by ID
	 */
	async getTask(id: number): Promise<ApiResponse<Task>> {
		return this.get<Task>(`/tasks/${id}`)
	}

	/**
	 * Create new task
	 */
	async createTask(data: CreateTaskRequest): Promise<ApiResponse<Task>> {
		return this.post<Task>('/tasks', data)
	}

	/**
	 * Get documents for a specific task
	 */
	async getTaskDocuments(taskId: number): Promise<ApiResponse<Document[]>> {
		return this.get<Document[]>(`/tasks/${taskId}/documents`)
	}

	/**
	 * Update workflow for a task
	 */
	async updateWorkflow(taskId: number): Promise<ApiResponse<WorkflowUpdateResponse>> {
		return this.put<WorkflowUpdateResponse>(`/tasks/${taskId}/workflow`)
	}

	/**
	 * Get workflow status for a task
	 */
	async getWorkflowStatus(taskId: number): Promise<ApiResponse<WorkflowUpdateResponse>> {
		return this.get<WorkflowUpdateResponse>(`/tasks/${taskId}/workflow-status`)
	}
}

/**
 * Document-related API operations
 * Matches: src/workers/routes/documents.ts
 */
class DocumentsApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Get specific document by ID
	 */
	async getDocument(id: number): Promise<ApiResponse<Document>> {
		return this.get<Document>(`/documents/${id}`)
	}

	/**
	 * Update document status (approve/reject)
	 */
	async updateDocumentStatus(
		id: number,
		data: UpdateDocumentStatusRequest
	): Promise<ApiResponse<void>> {
		return this.put<void>(`/documents/${id}`, data)
	}

	/**
	 * Update document content
	 */
	async updateDocumentContent(
		id: number,
		data: UpdateDocumentContentRequest
	): Promise<ApiResponse<void>> {
		console.log('[API] updateDocumentContent called', {
			timestamp: new Date().toISOString(),
			documentId: id,
			contentLength: data.content?.length || 0,
			contentPreview: data.content?.substring(0, 100) + '...'
		})
		
		try {
			const result = await this.put<void>(`/documents/${id}/content`, data)
			
			console.log('[API] updateDocumentContent response:', {
				success: result.success,
				error: result.error,
				timestamp: new Date().toISOString()
			})
			
			return result
		} catch (error) {
			console.error('[API] updateDocumentContent exception:', {
				error,
				errorMessage: error instanceof Error ? error.message : 'Unknown error',
				documentId: id,
				timestamp: new Date().toISOString()
			})
			throw error
		}
	}

	/**
	 * Get PDF URL for document viewing
	 * For authenticated clients, include token as query parameter
	 */
	getPDFUrl(id: number): string {
		return `${this.baseUrl}/documents/${id}/pdf`
	}

	/**
	 * Get authenticated PDF URL with token in query parameter
	 * Used when authentication token is available
	 */
	async getAuthenticatedPDFUrl(id: number): Promise<string> {
		if (this.getToken) {
			try {
				const token = await this.getToken()
				if (token) {
					return `${this.baseUrl}/documents/${id}/pdf?token=${encodeURIComponent(token)}`
				}
			} catch (error) {
				console.warn('Failed to get token for PDF URL:', error)
			}
		}
		return this.getPDFUrl(id)
	}
}

/**
 * Upload-related API operations
 * Matches: src/workers/routes/upload.ts
 */
class UploadApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Upload files for a task
	 * Note: This uses FormData, not JSON
	 */
	async uploadFiles(taskId: number, files: File[]): Promise<ApiResponse<UploadResponse>> {
		const formData = new FormData()
		for (const file of files) {
			formData.append('files', file)
		}

		return this.request<UploadResponse>(`/upload/${taskId}`, {
			method: 'POST',
			body: formData,
			headers: {}, // Let browser set Content-Type for FormData
		})
	}

	/**
	 * Upload a single image file and return the uploaded URL
	 * Used for court fee payment voucher image uploads
	 */
	async uploadImage(
		imageFile: File,
		options?: {
			folder?: string
			filename?: string
		}
	): Promise<ApiResponse<{ url: string; filename: string; size: number }>> {
		const formData = new FormData()

		// Use custom filename if provided
		const filename = options?.filename || imageFile.name
		const file = options?.filename ?
			new File([imageFile], filename, { type: imageFile.type }) :
			imageFile

		formData.append('image', file)

		if (options?.folder) {
			formData.append('folder', options.folder)
		}

		return this.request<{ url: string; filename: string; size: number }>('/upload/image', {
			method: 'POST',
			body: formData,
			headers: {}, // Let browser set Content-Type for FormData
		})
	}

	/**
	 * Upload image from blob data
	 */
	async uploadImageBlob(
		imageBlob: Blob,
		filename: string,
		options?: {
			folder?: string
		}
	): Promise<ApiResponse<{ url: string; filename: string; size: number }>> {
		const imageFile = new File([imageBlob], filename, {
			type: imageBlob.type,
			lastModified: Date.now()
		})

		return this.uploadImage(imageFile, options)
	}
}

/**
 * WFM integration API operations
 * Matches: src/workers/routes/wfm.ts
 */
class WFMApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Search WFM system for black case numbers
	 */
	async search(data: WFMSearchRequest): Promise<ApiResponse<WFMSearchResult>> {
		return this.post<WFMSearchResult>('/wfm/search', data)
	}

	/**
	 * Patch WFM tasks with document data
	 */
	async patchTasks(data: WFMPatchRequest): Promise<ApiResponse<WFMPatchResult>> {
		return this.post<WFMPatchResult>('/wfm/patch', data)
	}
}

/**
 * Debug and testing API operations
 * Matches: src/workers/routes/debug.ts
 */
class DebugApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Get system health status
	 */
	async getHealth(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
		return this.get('/debug/health')
	}

	/**
	 * Get storage statistics
	 */
	async getStorageStats(): Promise<ApiResponse<unknown>> {
		return this.get('/debug/storage-stats')
	}
}

/**
 * LED (Legal Executive Department) API operations
 * Matches: src/workers/routes/led/*.ts
 */
class LEDApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	// Cases API

	/**
	 * Get LED cases with filtering and pagination
	 */
	async getCases(params?: {
		status?: string
		court_name?: string
		assignee_id?: string
		search?: string
		page?: number
		limit?: number
	}): Promise<ApiResponse<LEDTasksResponse>> {
		const queryParams = new URLSearchParams()
		if (params?.status) queryParams.append('status', params.status)
		if (params?.court_name) queryParams.append('court_name', params.court_name)
		if (params?.assignee_id) queryParams.append('assignee_id', params.assignee_id)
		if (params?.search) queryParams.append('search', params.search)
		if (params?.page) queryParams.append('page', params.page.toString())
		if (params?.limit) queryParams.append('limit', params.limit.toString())

		const query = queryParams.toString()
		return this.get<LEDTasksResponse>(`/led/cases${query ? `?${query}` : ''}`)
	}

	/**
	 * Create new LED case
	 */
	async createCase(data: CreateLEDCaseRequest): Promise<ApiResponse<LEDCase>> {
		return this.request<LEDCase>('/led/cases', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		})
	}

	/**
	 * Get LED case by ID
	 */
	async getCase(id: string): Promise<ApiResponse<LEDTaskDetailResponse>> {
		return this.get<LEDTaskDetailResponse>(`/led/cases/${id}`)
	}

	/**
	 * Update LED case
	 */
	async updateCase(id: string, data: UpdateLEDCaseRequest): Promise<ApiResponse<LEDCase>> {
		return this.request<LEDCase>(`/led/cases/${id}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		})
	}

	/**
	 * Delete LED case
	 */
	async deleteCase(id: string): Promise<ApiResponse<void>> {
		return this.request<void>(`/led/cases/${id}`, {
			method: 'DELETE',
		})
	}

	// Dashboard API

	/**
	 * Get LED dashboard data
	 */
	async getDashboard(): Promise<ApiResponse<LEDDashboardData>> {
		return this.get<LEDDashboardData>('/led/dashboard')
	}

	// Status API

	/**
	 * Update case status
	 */
	async updateStatus(data: LEDStatusUpdateRequest): Promise<ApiResponse<LEDCase>> {
		return this.request<LEDCase>('/led/status', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		})
	}

	/**
	 * Batch update case statuses
	 */
	async batchUpdateStatus(data: LEDStatusUpdateRequest[]): Promise<ApiResponse<any>> {
		return this.request<any>('/led/status', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		})
	}

	// Documents API

	/**
	 * Get case documents
	 */
	async getDocuments(caseId: string, documentType?: string): Promise<ApiResponse<LEDDocument[]>> {
		const queryParams = new URLSearchParams({ case_id: caseId })
		if (documentType) queryParams.append('document_type', documentType)

		const query = queryParams.toString()
		return this.get<LEDDocument[]>(`/led/documents?${query}`)
	}

	/**
	 * Upload document
	 */
	async uploadDocument(
		caseId: string,
		documentType: string,
		file: File
	): Promise<ApiResponse<LEDDocument>> {
		const formData = new FormData()
		formData.append('case_id', caseId)
		formData.append('document_type', documentType)
		formData.append('file', file)

		return this.request<LEDDocument>('/led/documents', {
			method: 'POST',
			body: formData,
		})
	}

	/**
	 * Get document details
	 */
	async getDocument(id: string): Promise<ApiResponse<LEDDocument>> {
		return this.get<LEDDocument>(`/led/documents/${id}`)
	}

	/**
	 * Get PDF URL for document
	 */
	getPDFUrl(id: string): string {
		return `${this.baseUrl}/led/documents/${id}?view=pdf`
	}

	/**
	 * Delete document
	 */
	async deleteDocument(id: string): Promise<ApiResponse<void>> {
		return this.request<void>(`/led/documents/${id}`, {
			method: 'DELETE',
		})
	}
}

/**
 * Litigation API operations
 * Matches: src/workers/routes/litigation/*.ts
 */
class LitigationApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Get litigation dashboard data
	 */
	async getDashboard(): Promise<ApiResponse<import('../../shared/types').LitigationDashboard>> {
		return this.get<import('../../shared/types').LitigationDashboard>('/litigation/dashboard')
	}

	/**
	 * Get litigation cases with filters (using POST to handle Thai characters and complex searches)
	 */
	async getCases(params?: import('../../shared/types').GetLitigationCasesRequest): Promise<ApiResponse<import('../../shared/types').GetLitigationCasesResponse>> {
		return this.post<import('../../shared/types').GetLitigationCasesResponse>('/litigation/cases/search', params || {})
	}

	/**
	 * Get single case details with documents and timeline
	 */
	async getCase(id: string): Promise<ApiResponse<import('../../shared/types').LitigationCaseDetail>> {
		return this.get<import('../../shared/types').LitigationCaseDetail>(`/litigation/cases/${id}`)
	}

	/**
	 * Get documents for a case
	 */
	async getCaseDocuments(caseId: string): Promise<ApiResponse<{
		caseId: string
		courtDeptId: string
		blackCode: string
		documents: import('../../shared/types').LitigationDocument[]
		total: number
	}>> {
		return this.get(`/litigation/documents/${caseId}`)
	}

	/**
	 * Get document statistics for a case
	 */
	async getCaseDocumentStats(caseId: string): Promise<ApiResponse<any>> {
		return this.get(`/litigation/documents/stats/${caseId}`)
	}
}

/**
 * Statistics API operations
 * Matches: src/workers/routes/statistics.ts
 */
class StatisticsApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Get daily statistics for current user
	 */
	async getDailyStats(params?: { from?: string; to?: string }): Promise<ApiResponse<DailyStats[]>> {
		const queryParams = new URLSearchParams()
		if (params?.from) queryParams.append('from', params.from)
		if (params?.to) queryParams.append('to', params.to)

		const query = queryParams.toString()
		return this.get<DailyStats[]>(`/statistics/daily${query ? `?${query}` : ''}`)
	}

	/**
	 * Get summary statistics for current user
	 */
	async getSummary(period = '30'): Promise<ApiResponse<StatisticsSummary>> {
		return this.get<StatisticsSummary>(`/statistics/summary?period=${period}`)
	}

	/**
	 * Get statistics grouped by document type
	 */
	async getByDocumentType(params?: {
		from?: string
		to?: string
	}): Promise<ApiResponse<DocumentTypeStatistics>> {
		const queryParams = new URLSearchParams()
		if (params?.from) queryParams.append('from', params.from)
		if (params?.to) queryParams.append('to', params.to)

		const query = queryParams.toString()
		return this.get<DocumentTypeStatistics>(
			`/statistics/by-document-type${query ? `?${query}` : ''}`
		)
	}

	/**
	 * Get all users' statistics (admin only)
	 */
	async getUsersStats(params?: {
		from?: string
		to?: string
		limit?: string
	}): Promise<ApiResponse<UserStats[]>> {
		const queryParams = new URLSearchParams()
		if (params?.from) queryParams.append('from', params.from)
		if (params?.to) queryParams.append('to', params.to)
		if (params?.limit) queryParams.append('limit', params.limit)

		const query = queryParams.toString()
		return this.get<UserStats[]>(`/statistics/users${query ? `?${query}` : ''}`)
	}

	/**
	 * Get detailed users' statistics with document type breakdown
	 */
	async getUsersDetailedStats(params?: {
		from?: string
		to?: string
		limit?: string
	}): Promise<ApiResponse<UserDetailedStats[]>> {
		const queryParams = new URLSearchParams()
		if (params?.from) queryParams.append('from', params.from)
		if (params?.to) queryParams.append('to', params.to)
		if (params?.limit) queryParams.append('limit', params.limit)

		const query = queryParams.toString()
		return this.get<UserDetailedStats[]>(`/statistics/users-detailed${query ? `?${query}` : ''}`)
	}
}

/**
 * SLA (Service Level Agreement) API operations
 * Matches: src/workers/routes/sla/*.ts
 */
class SLAApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	// Cases API

	/**
	 * Get SLA cases with filtering
	 */
	async getCases(params?: {
		job_type?: string
		product_type?: string
		status?: string
		assignee_id?: string
	}): Promise<ApiResponse<{ data: LitigationCase[]; total: number }>> {
		const queryParams = new URLSearchParams()
		if (params?.job_type) queryParams.append('job_type', params.job_type)
		if (params?.product_type) queryParams.append('product_type', params.product_type)
		if (params?.status) queryParams.append('status', params.status)
		if (params?.assignee_id) queryParams.append('assignee_id', params.assignee_id)

		const query = queryParams.toString()
		return this.get(`/sla/cases${query ? `?${query}` : ''}`)
	}

	/**
	 * Create a new litigation case
	 */
	async createCase(data: {
		job_type: string
		product_type: string
		black_case_number?: string
		red_case_number?: string
		court_name?: string
		client_id?: string
		client_name?: string
	}): Promise<ApiResponse<LitigationCase>> {
		return this.post('/sla/cases', data)
	}

	/**
	 * Get case compliance status
	 */
	async getCaseCompliance(): Promise<ApiResponse<{
		summary: {
			totalCases: number
			compliantCases: number
			nonCompliantCases: number
			mixedCases: number
			complianceRate: number
		}
		cases: CaseComplianceStatus[]
	}>> {
		return this.get('/sla/cases/compliance')
	}

	/**
	 * Migrate cases to template flows
	 */
	async migrateCases(data: {
		case_ids: string[]
		options: {
			preserveProgress?: boolean
			preserveAssignments?: boolean
			preserveStartDates?: boolean
			dryRun?: boolean
			backupOriginal?: boolean
		}
	}): Promise<ApiResponse<any>> {
		return this.post('/sla/cases/migrate', data)
	}

	// Nodes API

	/**
	 * Get SLA nodes for a case
	 */
	async getNodes(caseId: string): Promise<ApiResponse<SLANode[]>> {
		return this.get(`/sla/nodes?case_id=${encodeURIComponent(caseId)}`)
	}

	/**
	 * Update node status
	 */
	async updateNode(nodeId: string, data: {
		status?: string
		progress?: number
		assignee_id?: string
		assignee_name?: string
	}): Promise<ApiResponse<SLANode>> {
		return this.put(`/sla/nodes/${nodeId}`, data)
	}

	/**
	 * Batch update nodes
	 */
	async batchUpdateNodes(data: {
		nodeIds: string[]
		updates: {
			status?: string
			progress?: number
			assignee_id?: string
			assignee_name?: string
		}
	}): Promise<ApiResponse<{ updated: number }>> {
		return this.post('/sla/nodes/batch-update', data)
	}

	// Assignees API

	/**
	 * Get assignee workload and performance data
	 */
	async getAssignees(params?: {
		timeRange?: string
		sortBy?: string
	}): Promise<ApiResponse<AssigneeWorkload[]>> {
		const queryParams = new URLSearchParams()
		if (params?.timeRange) queryParams.append('timeRange', params.timeRange)
		if (params?.sortBy) queryParams.append('sortBy', params.sortBy)

		const query = queryParams.toString()
		return this.get(`/sla/assignees${query ? `?${query}` : ''}`)
	}

	/**
	 * Get assignee suggestions for a task
	 */
	async getAssigneeSuggestions(params: {
		nodeId?: string
		taskType?: string
		requiredSkills?: string[]
		workloadThreshold?: number
	}): Promise<ApiResponse<{
		suggestions: Array<{
			assignee_id: string
			assignee_name: string
			currentWorkload: number
			efficiency: number
			matchScore: number
			reason: string
		}>
	}>> {
		return this.post('/sla/assignees/suggestions', params)
	}

	/**
	 * Assign tasks to users
	 */
	async assignTasks(data: {
		assignments: Array<{
			nodeId: string
			assignee_id: string
			assignee_name: string
		}>
	}): Promise<ApiResponse<{ assigned: number }>> {
		return this.post('/sla/assignees/assign', data)
	}

	// Flows API

	/**
	 * Get flow templates
	 */
	async getFlowTemplates(): Promise<ApiResponse<{ data: FlowTemplate[] }>> {
		return this.get('/sla/flows/templates')
	}

	/**
	 * Create or update flow template
	 */
	async saveFlowTemplate(template: FlowTemplate): Promise<ApiResponse<FlowTemplate>> {
		return this.post('/sla/flows/templates', template)
	}

	/**
	 * Instantiate a flow template for a case
	 */
	async instantiateFlow(data: {
		case_id: string
		template_code?: string
		skip_if_exists?: boolean
	}): Promise<ApiResponse<any>> {
		return this.post('/sla/flows/instantiate', data)
	}

	// Gantt API

	/**
	 * Get Gantt chart data for a case
	 */
	async getGanttData(caseId: string): Promise<ApiResponse<GanttTask[]>> {
		return this.get(`/sla/gantt?caseId=${encodeURIComponent(caseId)}`)
	}

	// Dependencies API

	/**
	 * Get dependencies for nodes
	 */
	async getDependencies(params?: {
		caseId?: string
		nodeId?: string
	}): Promise<ApiResponse<any[]>> {
		const queryParams = new URLSearchParams()
		if (params?.caseId) queryParams.append('caseId', params.caseId)
		if (params?.nodeId) queryParams.append('nodeId', params.nodeId)

		const query = queryParams.toString()
		return this.get(`/sla/dependencies${query ? `?${query}` : ''}`)
	}

	/**
	 * Validate dependency changes
	 */
	async validateDependencies(data: {
		dependencies: Array<{
			from_node_id: string
			to_node_id: string
			dependency_type: string
		}>
	}): Promise<ApiResponse<{
		valid: boolean
		issues: string[]
		warnings: string[]
	}>> {
		return this.post('/sla/dependencies/validate', data)
	}

	// Notifications API

	/**
	 * Get notifications for the current user
	 */
	async getNotifications(params?: {
		type?: string
		read?: boolean
		limit?: number
	}): Promise<ApiResponse<SLANotification[]>> {
		const queryParams = new URLSearchParams()
		if (params?.type) queryParams.append('type', params.type)
		if (params?.read !== undefined) queryParams.append('read', String(params.read))
		if (params?.limit) queryParams.append('limit', String(params.limit))

		const query = queryParams.toString()
		return this.get(`/sla/notifications${query ? `?${query}` : ''}`)
	}

	/**
	 * Mark notification as read
	 */
	async markNotificationRead(notificationId: number): Promise<ApiResponse<{ notification_id: number, read_at: string }>> {
		return this.post(`/sla/notifications/${notificationId}/read`, {})
	}

	/**
	 * Mark all notifications as read
	 */
	async markAllNotificationsRead(): Promise<ApiResponse<{ marked_count: number, marked_at: string }>> {
		return this.post('/sla/notifications/mark-all-read', {})
	}

	/**
	 * Get user notification preferences
	 */
	async getNotificationPreferences(): Promise<ApiResponse<any>> {
		return this.get('/sla/notifications/preferences')
	}

	/**
	 * Update notification preferences
	 */
	async updateNotificationPreferences(preferences: any): Promise<ApiResponse<any>> {
		return this.put('/sla/notifications/preferences', preferences)
	}

	// Reports API

	/**
	 * Get SLA metrics
	 */
	async getReportsMetrics(params?: {
		dateRange?: string
		jobType?: string
		productType?: string
	}): Promise<ApiResponse<{
		totalCases: number
		activeCases: number
		completedCases: number
		averageCompletionTime: number
		slaCompliance: number
		urgentTasks: number
		overdueTasks: number
		teamEfficiency: number
		complianceByJobType: Record<string, number>
		complianceByProductType: Record<string, number>
	}>> {
		const queryParams = new URLSearchParams()
		if (params?.dateRange) queryParams.append('dateRange', params.dateRange)
		if (params?.jobType) queryParams.append('jobType', params.jobType)
		if (params?.productType) queryParams.append('productType', params.productType)

		const query = queryParams.toString()
		return this.get(`/sla/reports/metrics${query ? `?${query}` : ''}`)
	}

	/**
	 * Get performance trends
	 */
	async getReportsPerformanceTrends(params?: {
		dateRange?: string
		jobType?: string
		productType?: string
	}): Promise<ApiResponse<Array<{
		date: string
		completed: number
		started: number
		slaBreached: number
		onTime: number
		efficiency: number
	}>>> {
		const queryParams = new URLSearchParams()
		if (params?.dateRange) queryParams.append('dateRange', params.dateRange)
		if (params?.jobType) queryParams.append('jobType', params.jobType)
		if (params?.productType) queryParams.append('productType', params.productType)

		const query = queryParams.toString()
		return this.get(`/sla/reports/performance-trends${query ? `?${query}` : ''}`)
	}

	/**
	 * Get team performance
	 */
	async getReportsTeamPerformance(params?: {
		dateRange?: string
		jobType?: string
		productType?: string
	}): Promise<ApiResponse<Array<{
		assigneeId: string
		assigneeName: string
		tasksCompleted: number
		averageTime: number
		slaCompliance: number
		efficiency: number
		workloadCapacity: number
	}>>> {
		const queryParams = new URLSearchParams()
		if (params?.dateRange) queryParams.append('dateRange', params.dateRange)
		if (params?.jobType) queryParams.append('jobType', params.jobType)
		if (params?.productType) queryParams.append('productType', params.productType)

		const query = queryParams.toString()
		return this.get(`/sla/reports/team-performance${query ? `?${query}` : ''}`)
	}

	/**
	 * Get case distribution
	 */
	async getReportsCaseDistribution(params?: {
		dateRange?: string
	}): Promise<ApiResponse<Array<{
		jobType: string
		productType: string
		count: number
		percentage: number
		avgDuration: number
		complianceRate: number
	}>>> {
		const queryParams = new URLSearchParams()
		if (params?.dateRange) queryParams.append('dateRange', params.dateRange)

		const query = queryParams.toString()
		return this.get(`/sla/reports/case-distribution${query ? `?${query}` : ''}`)
	}

	/**
	 * Export SLA reports
	 */
	async exportReports(data: {
		format: 'pdf' | 'excel' | 'csv'
		reportType: string
		dateRange: string
		filters: Record<string, any>
	}): Promise<ApiResponse<{
		success: boolean
		message: string
		data: {
			format: string
			reportType: string
			dateRange: string
			filters: Record<string, any>
			estimatedSize: string
			estimatedTime: string
		}
	}>> {
		return this.post('/sla/reports/export', data)
	}
}

/**
 * Pay-in Slip API for managing court petition pay-in slips
 */
class PayinSlipApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	// Build composite key URL
	private buildKeyUrl(key: PayinSlipKey): string {
		return `${encodeURIComponent(key.blackNo)}/${encodeURIComponent(key.redNo)}/${encodeURIComponent(key.court)}/${encodeURIComponent(key.status)}`
	}

	/**
	 * Sync with external API and get unpaid slips
	 */
	async syncAndGetUnpaidSlips(): Promise<ApiResponse<PayinSlip[]>> {
		return this.request<PayinSlip[]>('/payin/slips')
	}

	/**
	 * Get specific slip by composite key
	 */
	async getSlip(key: PayinSlipKey): Promise<ApiResponse<PayinSlip>> {
		const url = `/payin/slips/${this.buildKeyUrl(key)}`
		return this.request<PayinSlip>(url)
	}

	/**
	 * Upload payment receipt for a slip
	 */
	async uploadPayment(key: PayinSlipKey, formData: FormData): Promise<ApiResponse<void>> {
		const url = `/payin/slips/${this.buildKeyUrl(key)}/upload-payment`
		const token = this.getToken ? await this.getToken(0) : null

		const headers: Record<string, string> = {}
		
		if (token) {
			headers['Authorization'] = `Bearer ${token}`
		}
		
		// Add company header if available
		if (this.getCompany) {
			const company = this.getCompany()
			if (company) {
				headers['X-Company-ID'] = company
			}
		}
		
		const response = await fetch(`${this.baseUrl}${url}`, {
			method: 'POST',
			headers,
			body: formData,
		})

		if (!response.ok) {
			const error = await response.text()
			throw new Error(error || `HTTP ${response.status}`)
		}

		return response.json()
	}

	/**
	 * Get authenticated URL for confiscation PDF
	 */
	async getConfiscationPdfUrl(key: PayinSlipKey): Promise<string> {
		const token = this.getToken ? await this.getToken(0) : null
		const url = `/payin/slips/${this.buildKeyUrl(key)}/confiscation-pdf`
		const params = token ? `?token=${encodeURIComponent(token)}` : ''
		return `${this.baseUrl}${url}${params}`
	}
}

/**
 * Courts API operations
 * Matches: src/workers/routes/courts.ts
 */
class CourtsApi extends BaseApiClient {
	constructor(baseUrl = '/api', getToken?: () => Promise<string | null>, getCompany?: () => string | null) {
		super(baseUrl, getToken, getCompany)
	}

	/**
	 * Get all courts
	 */
	async getCourts(): Promise<ApiResponse<{ id: number; name: string }[]>> {
		return this.get<{ id: number; name: string }[]>('/courts')
	}
}

/**
 * Main HonoApiClient class that combines all API modules
 * Provides a clean, organized interface matching the Hono route structure
 */
export class HonoApiClient extends BaseApiClient {
	// API module instances
	public readonly tasks: TasksApi
	public readonly documents: DocumentsApi
	public readonly upload: UploadApi
	public readonly wfm: WFMApi
	public readonly debug: DebugApi
	public readonly statistics: StatisticsApi
	public readonly courts: CourtsApi
	public readonly led: LEDApi
	public readonly litigation: LitigationApi
	public readonly sla: SLAApi
	public readonly payinSlips: PayinSlipApi

	constructor(
		baseUrl = '/api', 
		getToken?: (retryAttempt?: number) => Promise<string | null>,
		getCompany?: () => string | null
	) {
		super(baseUrl, getToken, getCompany)

		// Initialize all API modules with the same base URL, token getter, and company getter
		this.tasks = new TasksApi(baseUrl, getToken, getCompany)
		this.documents = new DocumentsApi(baseUrl, getToken, getCompany)
		this.upload = new UploadApi(baseUrl, getToken, getCompany)
		this.wfm = new WFMApi(baseUrl, getToken, getCompany)
		this.debug = new DebugApi(baseUrl, getToken, getCompany)
		this.statistics = new StatisticsApi(baseUrl, getToken, getCompany)
		this.courts = new CourtsApi(baseUrl, getToken, getCompany)
		this.led = new LEDApi(baseUrl, getToken, getCompany)
		this.litigation = new LitigationApi(baseUrl, getToken, getCompany)
		this.sla = new SLAApi(baseUrl, getToken, getCompany)
		this.payinSlips = new PayinSlipApi(baseUrl, getToken, getCompany)
	}

	// Legacy compatibility methods (can be removed in future versions)

	/**
	 * @deprecated Use honoApiClient.tasks.getTasks() instead
	 */
	async getTasks(params?: GetTasksRequest): Promise<ApiResponse<Task[]>> {
		return this.tasks.getTasks(params)
	}

	/**
	 * @deprecated Use honoApiClient.tasks.getTask(id) instead
	 */
	async getTask(id: number): Promise<ApiResponse<Task>> {
		return this.tasks.getTask(id)
	}

	/**
	 * @deprecated Use honoApiClient.documents.getDocument(id) instead
	 */
	async getDocument(id: number): Promise<ApiResponse<Document>> {
		return this.documents.getDocument(id)
	}

	/**
	 * @deprecated Use honoApiClient.documents.getPDFUrl(id) instead
	 */
	getPDFUrl(id: number): string {
		return this.documents.getPDFUrl(id)
	}
}

// Export factory function to create authenticated client
export function createAuthenticatedApiClient(
	getToken: (retryAttempt?: number) => Promise<string | null>,
	getCompany?: () => string | null
) {
	return new HonoApiClient('/api', getToken, getCompany)
}

// Export non-authenticated singleton instance for backward compatibility
export const honoApiClient = new HonoApiClient()

// Also export individual API classes for advanced usage
export { TasksApi, DocumentsApi, UploadApi, WFMApi, DebugApi, StatisticsApi, LEDApi, LitigationApi, SLAApi }
